/* global G */
import { curry } from 'lib/util'
import sequenceComponentState from 'lib/sequence/component/state'

const {
  set: setHelperText,
} = sequenceComponentState('helperText')

const {
  set: setError,
} = sequenceComponentState('error')

/**
 * Returns the translation of the text to show when a service item already exists.
 *
 * @param {Gaia.AppModule.Spec} module  the current module composition object
 * @returns {Promise<string>}           the translated string
 */
const invalidSerialNumberText = module => module[G.ADAPTER][G.INTL]._t(
  'serialField.invalidSerialNumber',
  {
    ns: 'device',
    _key: 'serialField.invalidSerialNumber',
    defaultValue: 'Invalid serial number',
  },
)

/**
 * Returns the translation of the text to show when a service item already exists.
 *
 * @param {Gaia.AppModule.Spec} module  the current module composition object
 * @param {string} organisation         the name of the organisation the existing service item is
 *                                      installed at
 * @returns {Promise<string>}           the translated string
 */
const deviceFoundText = (module, organisation) => module[G.ADAPTER][G.INTL]._t(
  'serialField.deviceFound',
  {
    ns: 'device',
    _key: 'serialField.deviceFound',
    defaultValue: 'Device is already assigned to {{organisation}}',
    organisation,
  },
)

/**
 * Returns the translation of the text to show when no service item or equipment is found.
 *
 * @param {Gaia.AppModule.Spec} module  the current module composition object
 * @returns {Promise<string>}           the translated string
 */
const deviceNotFoundText = module => module[G.ADAPTER][G.INTL]._t(
  'serialField.deviceNotFound',
  {
    ns: 'device',
    _key: 'serialField.deviceNotFound',
    defaultValue: 'Device cannot be identified from production data',
  },
)

/**
 * Updates a serial field's helperText and error state properties according to the result of the
 * device's {@link search}.
 *
 * @param {Gaia.AppModule.Spec} module    the current module composition object
 * @param {Gaia.Component.Spec} component the current action's main component
 * @param {Object} result                 the result of the {@link search} function
 */
export default curry(async (module, component, eventOrResult) => {
  const [serial, result] = eventOrResult?.detail || eventOrResult
  const { invalid, serviceItem, installedAt } = result || {}
  const serviceItemExists = serviceItem && serviceItem._rev?.charAt(0) !== '0'

  if (invalid) {
    setHelperText(component, await invalidSerialNumberText(module))
    setError(component, true)
  } else if (serial && serviceItemExists) {
    const organisation = installedAt?.value?.name
    setHelperText(component, await deviceFoundText(module, organisation))
    setError(component, true)
  } else if (serial && !serviceItem /* && !settings.suppressEquipmentDevices */) {
    setHelperText(component, await deviceNotFoundText(module))
    setError(component, true)
  } else {
    setHelperText(component, '')
    setError(component, false)
  }

  return result
})
